import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { SignedInRedirectComponent } from 'app/core/auth/signed-in-redirect.component';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { RoleGuard } from 'app/core/auth/guards/role.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { UserRole } from 'app/core/user/user.types';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path (role-based destination via SignedInRedirectComponent)
  { path: '', pathMatch: 'full', redirectTo: 'signed-in-redirect' },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () =>
          import('app/modules/auth/confirmation-required/confirmation-required.routes')
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')
      },
      {
        path: 'reset-password',
        loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')
      },
      { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
      { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') }
    ]
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [
      { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
      {
        path: 'unlock-session',
        loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')
      },
      // Enrolamiento de MFA (LION-521): pantalla autenticada, layout vacío.
      {
        path: 'mfa-setup',
        loadChildren: () => import('app/modules/auth/mfa-setup/mfa-setup.routes')
      }
    ]
  },

  // Admin routes
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver
    },
    children: [
      {
        path: 'signed-in-redirect',
        component: SignedInRedirectComponent
      },
      {
        path: 'home',
        loadChildren: () => import('app/modules/landing/home/home.routes')
      },
      // Mi Perfil (LION-631): autogestión de cuenta para CUALQUIER usuario
      // autenticado. Vive en el layout de la app (sidebar + header + navegación),
      // sin RoleGuard. Movida desde el grupo 'layout vacío' (LION-631 follow-up).
      {
        path: 'profile',
        loadChildren: () => import('app/modules/auth/profile/profile.routes')
      },
      {
        path: 'dashboard',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
        loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes')
      },
      {
        path: 'users',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN, UserRole.SUPPORT] },
        loadChildren: () => import('app/modules/admin/user/user.routes')
      },
      {
        path: 'companies',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
        loadChildren: () => import('app/modules/admin/company/company.routes')
      },
      {
        path: 'vehicles',
        loadChildren: () => import('app/modules/admin/vehicle/vehicle.routes')
      },
      {
        path: 'equipment',
        loadChildren: () => import('app/modules/admin/equipment/equipment.routes')
      },
      {
        path: 'events',
        loadChildren: () => import('app/modules/admin/event/event.routes')
      },
      {
        path: 'schedules',
        loadChildren: () => import('app/modules/admin/schedule/schedule.routes')
      },
      {
        // El módulo compulsas entero es de PVP: la compulsa es una competencia
        // entre talleres que PVP administra de punta a punta. El cliente (ADMIN)
        // ve su turno y el evento que sale de la adjudicación, no las ofertas.
        path: 'compulsas',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN] },
        loadChildren: () => import('app/modules/admin/compulsa/compulsa.routes')
      },
      {
        path: 'activity-logs',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
        loadChildren: () => import('app/modules/admin/activity-log/activity-log.routes')
      },
      {
        path: 'cost-centers',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
        loadChildren: () => import('app/modules/admin/cost-center/cost-center.routes')
      },
      {
        path: 'reprogramming-reasons',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN] },
        loadChildren: () => import('app/modules/admin/reprogramming-reason/reprogramming-reason.routes')
      },
      {
        path: 'articles',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN] },
        loadChildren: () => import('app/modules/admin/articles/articles.routes')
      },
      {
        path: 'article-groups',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN] },
        loadChildren: () => import('app/modules/admin/article-groups/article-groups.routes')
      },
      {
        path: 'delegaciones',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
        loadChildren: () => import('app/modules/admin/delegacion/delegacion.routes')
      },
      {
        path: 'budget-approval-rules',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.SUPER_ADMIN, UserRole.MASTER] },
        loadChildren: () => import('app/modules/admin/budget-approval-rules/budget-approval-rules.routes')
      },
      {
        path: 'vehicle-workshops',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.MASTER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
        loadChildren: () => import('app/modules/admin/vehicle-workshop/vehicle-workshop.routes')
      },
      {
        path: 'kanban',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.WORKSHOP] },
        loadChildren: () => import('app/modules/admin/kanban/kanban.routes')
      },
      {
        path: 'services',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.WORKSHOP] },
        loadChildren: () => import('app/modules/admin/services/services.routes')
      },
      {
        path: 'facturacion',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.SUPER_ADMIN, UserRole.MASTER] },
        loadChildren: () => import('app/modules/admin/billing/billing.routes')
      },
      {
        path: 'ordenes-de-pago',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.SUPER_ADMIN, UserRole.MASTER] },
        loadChildren: () => import('app/modules/admin/presentations-pvp/presentations-pvp.routes')
      },
      {
        path: 'presentaciones',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.WORKSHOP] },
        loadChildren: () => import('app/modules/admin/presentations/presentations.routes')
      },
      {
        // Bandeja de compulsas del taller (LION-692/694/695/696): cotiza su
        // oferta sin recibir el vehículo.
        path: 'mis-compulsas',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.WORKSHOP] },
        loadChildren: () => import('app/modules/admin/quotes-workshop/quotes-workshop.routes')
      },
      {
        path: 'mis-facturas',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.WORKSHOP] },
        loadChildren: () => import('app/modules/admin/invoices-workshop/invoices-workshop.routes')
      },
      {
        path: 'workshop-dashboard',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.WORKSHOP] },
        loadChildren: () => import('app/modules/admin/workshop-dashboard/workshop-dashboard.routes')
      },
      {
        path: 'unfactured-events',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.WORKSHOP] },
        loadChildren: () => import('app/modules/admin/unfactured-events/unfactured-events.routes')
      },
      {
        path: 'reports',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MASTER] },
        loadChildren: () => import('app/modules/admin/reports/reports.routes')
      },
      {
        // Herramienta de migración de un solo uso: carga los eventos históricos
        // del sistema anterior. Para darla de baja, borrar esta ruta junto con
        // app/modules/admin/legacy-import/ y app/core/legacy-import/.
        path: 'legacy-import',
        canActivate: [RoleGuard],
        data: { roles: [UserRole.SUPER_ADMIN, UserRole.MASTER] },
        loadChildren: () => import('app/modules/admin/legacy-import/legacy-import.routes')
      }
    ]
  }
];
